// Office > Documents (rule 37): the register of controlled quality documents, the files in the library nobody has
// registered, and the document types and processes Quality keeps. SharePoint holds the masters; a person meets it only
// when they press Open in Word. Rule 15: a library never read says so, and a failed read is shown beside the kept list.
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DataTable } from '@/components/DataTable';
import { api, errorMessage } from '../../lib/api';
import { actionLabel } from '../../lib/vocabulary';
import type { AuthAction } from '../../lib/types';
import { fieldClass, labelClass, officeFocus, serverMessage, useMyGrants, useResource } from '../../shells/OfficeShell';
import { TONE_CLASS, chipClass, stamp } from './goodsInModel';
import {
  APPLIES_TO_GRANTS,
  GATE_LABEL,
  STATUS_LABEL,
  STATUS_TONE,
  applyIntake,
  attentionOf,
  emptyRegisterForm,
  registerProblems,
  toRegistration,
  uncheckedAi,
  type AiField,
  type DocumentsConfig,
  type IntakeSuggestion,
  type LibraryFile,
  type RegisterEntry,
  type RegisterForm,
  type RegisterPayload,
  type ShownStatus,
  type Taxonomy,
} from './documentsModel';

const col = createColumnHelper<RegisterEntry>();
const muted = 'm-0 text-(length:--fs-sm) text-(--muted)';

/** One field of the register form, marked while it holds a value Claude read that nobody has checked. */
function FormField({ label, children, ai, onConfirm }: { label: string; children: React.ReactNode; ai?: boolean; onConfirm?: () => void }) {
  return (
    <div className={`flex flex-col gap-(--s1) ${ai ? 'rounded-(--radius-sm) outline-2 outline-offset-2 outline-(--warn-line)' : ''}`}>
      <label className="flex flex-col gap-(--s1)">
        <span className={labelClass}>{label}</span>
        {children}
      </label>
      {ai ? (
        <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
          <input type="checkbox" onChange={onConfirm} /> Read by AI: checked against the file
        </label>
      ) : null}
    </div>
  );
}

function RegisterSheet({ file, tax, config, onClose, onDone }: { file: LibraryFile | null; tax: Taxonomy; config: DocumentsConfig | null; onClose: () => void; onDone: () => void }) {
  const [f, setF] = useState<RegisterForm>(() => emptyRegisterForm(file));
  const [busy, setBusy] = useState(false);
  const [reading, setReading] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const problems = registerProblems(f);
  const unchecked = new Set(uncheckedAi(f));
  const set = (patch: Partial<RegisterForm>) => setF((x) => ({ ...x, ...patch }));
  const types = tax.types.filter((t) => !t.retired);
  const processes = tax.processes.filter((p) => !p.retired);
  const toggle = (list: string[], v: string) => (list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);
  const aiProps = (k: AiField) => ({ ai: unchecked.has(k), onConfirm: () => setF((x) => ({ ...x, confirmed: { ...x.confirmed, [k]: true } })) });

  async function readWithAi() {
    if (!file) return;
    setReading(true);
    setFailed(null);
    try {
      const out = await api.post<{ suggestion: IntakeSuggestion }>('/documents/prefill', { itemId: file.itemId });
      const fileTitle = emptyRegisterForm(file).title;
      setF((x) => applyIntake(x, out.suggestion, fileTitle));
    } catch (err) {
      setFailed(serverMessage(err));
    } finally {
      setReading(false);
    }
  }

  async function save() {
    setBusy(true);
    setFailed(null);
    try {
      await api.post('/documents', toRegistration(f));
      onDone();
      onClose();
    } catch (err) {
      setFailed(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Sheet open onOpenChange={(o) => (o ? null : onClose())}>
      <SheetContent side="right" className="flex w-full flex-col gap-(--s3) overflow-y-auto sm:max-w-[640px]">
        <SheetHeader>
          <SheetTitle>{file ? 'Register this file' : 'New document from a template'}</SheetTitle>
          <SheetDescription className={file ? 'font-mono' : ''}>{file ? file.name : "A copy of the type's template goes into the Controlled library under its new number."}</SheetDescription>
        </SheetHeader>
        {file && config?.intakeAi ? (
          <div className="flex flex-wrap items-center gap-(--s2)">
            <Button type="button" variant="outline" disabled={reading} onClick={() => void readWithAi()}>
              {reading ? 'Reading…' : 'Read with AI'}
            </Button>
            <p className={muted}>A suggestion: you check every value against the file before Save.</p>
          </div>
        ) : null}
        <div className="grid gap-(--s3) sm:grid-cols-2">
          <FormField label="Type" {...aiProps('typeCode')}>
            <select className={`${fieldClass} ${officeFocus}`} value={f.typeCode} onChange={(e) => set({ typeCode: e.target.value })}>
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.code} value={t.code}>
                  {t.code} · {t.name}
                </option>
              ))}
            </select>
          </FormField>
          <FormField label="Process" {...aiProps('processCode')}>
            <select className={`${fieldClass} ${officeFocus}`} value={f.processCode} onChange={(e) => set({ processCode: e.target.value })}>
              <option value="">Choose…</option>
              {processes.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.code} · {p.name}
                </option>
              ))}
            </select>
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="Title" {...aiProps('title')}>
              <Input value={f.title} onChange={(e) => set({ title: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Owner role" {...aiProps('ownerRole')}>
            <Input value={f.ownerRole} onChange={(e) => set({ ownerRole: e.target.value })} placeholder="The role, not a person" />
          </FormField>
          <FormField label="Approver role" {...aiProps('approverRole')}>
            <Input value={f.approverRole} onChange={(e) => set({ approverRole: e.target.value })} />
          </FormField>
          <div className="sm:col-span-2">
            <FormField label="Reviewer roles (comma between)">
              <Input value={f.reviewerRoles} onChange={(e) => set({ reviewerRoles: e.target.value })} />
            </FormField>
          </div>
          <FormField label="Old document code" {...aiProps('legacyCode')}>
            <Input value={f.legacyCode} onChange={(e) => set({ legacyCode: e.target.value })} />
          </FormField>
          <FormField label="Old version" {...aiProps('legacyVersion')}>
            <Input value={f.legacyVersion} onChange={(e) => set({ legacyVersion: e.target.value })} />
          </FormField>
          <FormField label="D365 asset, if it is about one machine">
            <Input value={f.assetId} onChange={(e) => set({ assetId: e.target.value })} />
          </FormField>
          {file ? (
            <label className="flex min-h-[44px] items-center gap-(--s2) self-end text-(length:--fs-sm)">
              <input type="checkbox" checked={f.imported} onChange={(e) => set({ imported: e.target.checked })} /> Imported as found, not yet approved here
            </label>
          ) : null}
        </div>
        <fieldset className="m-0 border-0 p-0">
          <legend className={labelClass}>Who must read it: everyone holding…</legend>
          <div className="mt-(--s1) grid gap-x-(--s3) sm:grid-cols-2">
            {APPLIES_TO_GRANTS.map((g) => (
              <label key={g} className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
                <input type="checkbox" checked={f.grants.includes(g)} onChange={() => set({ grants: toggle(f.grants, g) })} />
                {actionLabel(g as AuthAction)}
              </label>
            ))}
          </div>
        </fieldset>
        <FormField label="…and these people (comma between)">
          <Input value={f.upns} onChange={(e) => set({ upns: e.target.value })} placeholder="name@majans.com" />
        </FormField>
        <fieldset className="m-0 border-0 p-0">
          <legend className={labelClass}>App gates this procedure governs</legend>
          <div className="mt-(--s1) grid gap-x-(--s3) sm:grid-cols-2">
            {(config?.gates ?? Object.keys(GATE_LABEL)).map((g) => (
              <label key={g} className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
                <input type="checkbox" checked={f.governs.includes(g)} onChange={() => set({ governs: toggle(f.governs, g) })} />
                {GATE_LABEL[g] ?? g}
              </label>
            ))}
          </div>
        </fieldset>
        {problems.length ? (
          <ul className={`${muted} list-disc pl-(--s4)`}>
            {problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        ) : null}
        {failed ? (
          <p role="alert" className="m-0 font-semibold text-(--bad)">
            {failed}
          </p>
        ) : null}
        <div className="flex flex-wrap gap-(--s2)">
          <Button type="button" disabled={busy || problems.length > 0} onClick={() => void save()}>
            {busy ? 'Saving…' : 'Register'}
          </Button>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function UnregisteredFiles({ files, mayWrite, onRegister, onDone }: { files: LibraryFile[]; mayWrite: boolean; onRegister: (f: LibraryFile) => void; onDone: () => void }) {
  const [dismissing, setDismissing] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [failed, setFailed] = useState<string | null>(null);
  if (!files.length) return null;
  async function dismiss(itemId: string) {
    setFailed(null);
    try {
      await api.post(`/documents/files/${encodeURIComponent(itemId)}/dismiss`, { note });
      setDismissing(null);
      setNote('');
      onDone();
    } catch (err) {
      setFailed(serverMessage(err));
    }
  }
  return (
    <section aria-label="Files not registered" className="flex flex-col gap-(--s2) rounded-(--radius) border border-(--warn-line) bg-(--warn-bg) p-(--s3)">
      <h2 className="m-0 text-(length:--fs-md) font-semibold">
        {files.length} file{files.length === 1 ? '' : 's'} in the library {files.length === 1 ? 'is' : 'are'} not registered
      </h2>
      <p className={muted}>Until it is registered, nobody can tell a file here is controlled. Register it, or say what it is.</p>
      <ul className="m-0 flex list-none flex-col gap-(--s2) p-0">
        {files.map((f) => (
          <li key={f.itemId} className="flex flex-wrap items-center gap-(--s2)">
            <span className="min-w-0 flex-1 font-mono text-(length:--fs-sm)">
              {f.webUrl ? (
                <a href={f.webUrl} target="_blank" rel="noopener noreferrer" className="underline">
                  {f.name}
                </a>
              ) : (
                f.name
              )}
              <span className="ml-(--s2) font-sans text-(--muted)">{f.modifiedByUpn ?? ''}</span>
            </span>
            {mayWrite ? (
              dismissing === f.itemId ? (
                <span className="flex flex-wrap items-center gap-(--s2)">
                  <Input aria-label={`What is ${f.name}?`} value={note} onChange={(e) => setNote(e.target.value)} placeholder="What is it?" className="w-[220px]" />
                  <Button type="button" variant="outline" size="sm" disabled={!note.trim()} onClick={() => void dismiss(f.itemId)}>
                    Save
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setDismissing(null)}>
                    Cancel
                  </Button>
                </span>
              ) : (
                <>
                  <Button type="button" size="sm" onClick={() => onRegister(f)}>
                    Register
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => setDismissing(f.itemId)}>
                    Not a controlled document
                  </Button>
                </>
              )
            ) : null}
          </li>
        ))}
      </ul>
      {failed ? (
        <p role="alert" className="m-0 font-semibold text-(--bad)">
          {failed}
        </p>
      ) : null}
    </section>
  );
}

/** The document types and processes, Quality's to keep. Empty until they have agreed them. */
function TaxonomyPanel({ tax, mayEdit, onDone }: { tax: Taxonomy; mayEdit: boolean; onDone: () => void }) {
  const [kind, setKind] = useState<'TYPE' | 'PROCESS'>('TYPE');
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [months, setMonths] = useState('24');
  const [sections, setSections] = useState('');
  const [template, setTemplate] = useState('');
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  function edit(k: 'TYPE' | 'PROCESS', c: string) {
    setKind(k);
    setCode(c);
    const t = tax.types.find((x) => x.code === c);
    const p = tax.processes.find((x) => x.code === c);
    setName((k === 'TYPE' ? t?.name : p?.name) ?? '');
    setMonths(String(t?.reviewMonths ?? 24));
    setSections((t?.sections ?? []).join(', '));
    setTemplate(t?.templateItemId ?? '');
  }

  async function save(retired = false) {
    setBusy(true);
    setFailed(null);
    try {
      await api.post('/documents/taxonomy', {
        kind,
        code,
        name,
        reviewMonths: Number(months),
        sections: sections
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        templateItemId: template.trim() || undefined,
        retired,
      });
      setCode('');
      setName('');
      setSections('');
      setTemplate('');
      onDone();
    } catch (err) {
      setFailed(serverMessage(err));
    } finally {
      setBusy(false);
    }
  }

  const chips = (k: 'TYPE' | 'PROCESS', rows: Array<{ code: string; name: string; retired: boolean; reviewMonths?: number }>) =>
    rows.length ? (
      <ul className="m-0 flex list-none flex-wrap gap-(--s2) p-0">
        {rows.map((r) => (
          <li key={r.code}>
            <button type="button" disabled={!mayEdit} onClick={() => edit(k, r.code)} className={`${chipClass} ${r.retired ? TONE_CLASS.quiet : 'border-(--border)'} ${officeFocus}`} title={r.retired ? 'Retired' : undefined}>
              {r.code} · {r.name}
              {r.reviewMonths ? ` · ${r.reviewMonths} mo` : ''}
              {r.retired ? ' (retired)' : ''}
            </button>
          </li>
        ))}
      </ul>
    ) : (
      <p className={muted}>None yet.</p>
    );

  return (
    <details className="rounded-(--radius) border border-(--border) bg-(--card) p-(--s3)" open={tax.types.length === 0 || tax.processes.length === 0}>
      <summary className="cursor-pointer font-semibold">Document types and processes</summary>
      <div className="mt-(--s3) flex flex-col gap-(--s3)">
        {tax.types.length === 0 || tax.processes.length === 0 ? <p className={muted}>Nothing can be registered until Quality has agreed these. They are data: a change never needs a release of the app.</p> : null}
        <div>
          <h3 className={`${labelClass} m-0 mb-(--s1)`}>Types</h3>
          {chips('TYPE', tax.types)}
        </div>
        <div>
          <h3 className={`${labelClass} m-0 mb-(--s1)`}>Processes</h3>
          {chips('PROCESS', tax.processes)}
        </div>
        {mayEdit ? (
          <div className="grid gap-(--s3) sm:grid-cols-[auto_8rem_1fr]">
            <label className="flex flex-col gap-(--s1)">
              <span className={labelClass}>Adding</span>
              <select className={`${fieldClass} ${officeFocus}`} value={kind} onChange={(e) => setKind(e.target.value as 'TYPE' | 'PROCESS')}>
                <option value="TYPE">Type</option>
                <option value="PROCESS">Process</option>
              </select>
            </label>
            <label className="flex flex-col gap-(--s1)">
              <span className={labelClass}>Code</span>
              <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} />
            </label>
            <label className="flex flex-col gap-(--s1)">
              <span className={labelClass}>Name</span>
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </label>
            {kind === 'TYPE' ? (
              <>
                <label className="flex flex-col gap-(--s1)">
                  <span className={labelClass}>Review cycle (months)</span>
                  <Input type="number" min={1} max={120} value={months} onChange={(e) => setMonths(e.target.value)} />
                </label>
                <label className="flex flex-col gap-(--s1) sm:col-span-2">
                  <span className={labelClass}>Sections it should hold (comma between)</span>
                  <Input value={sections} onChange={(e) => setSections(e.target.value)} />
                </label>
                <label className="flex flex-col gap-(--s1) sm:col-span-3">
                  <span className={labelClass}>Template's library item id</span>
                  <Input value={template} onChange={(e) => setTemplate(e.target.value)} />
                </label>
              </>
            ) : null}
            <div className="flex flex-wrap gap-(--s2) sm:col-span-3">
              <Button type="button" disabled={busy || !code.trim() || !name.trim()} onClick={() => void save()}>
                Save
              </Button>
              <Button type="button" variant="outline" disabled={busy || !code.trim() || !name.trim()} onClick={() => void save(true)}>
                Save as retired
              </Button>
            </div>
          </div>
        ) : null}
        {failed ? (
          <p role="alert" className="m-0 font-semibold text-(--bad)">
            {failed}
          </p>
        ) : null}
      </div>
    </details>
  );
}

export function Documents() {
  const config = useResource<DocumentsConfig>('/documents/config');
  const res = useResource<RegisterPayload>(config.data?.enabled ? '/documents' : null);
  const grants = useMyGrants();
  const [registering, setRegistering] = useState<{ file: LibraryFile | null } | null>(null);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState<string | null>(null);
  const [status, setStatus] = useState<ShownStatus | 'ANY'>('ANY');
  const [showRetired, setShowRetired] = useState(false);
  useEffect(() => setSyncNote(null), [res.data]);

  const mayAuthor = grants.known && grants.holds('DOC_CONTROL');
  const mayApprove = grants.known && grants.holds('DOC_APPROVE');
  const mayAudit = grants.known && (mayAuthor || mayApprove || grants.holds('RELEASE'));
  const tax = res.data?.taxonomy ?? { types: [], processes: [] };
  const library = res.data?.library ?? null;
  const rows = (res.data?.documents ?? []).filter((d) => (showRetired || !d.retired) && (status === 'ANY' || d.status === status));
  const canRegister = tax.types.some((t) => !t.retired) && tax.processes.some((p) => !p.retired);
  const hasTemplate = tax.types.some((t) => !t.retired && t.templateItemId);

  async function sync() {
    setSyncing(true);
    setSyncNote(null);
    try {
      const out = await api.post<{ ok: boolean; files?: number; reason?: string }>('/documents/sync', {});
      res.reload();
      if (!out.ok) setSyncNote(`The library could not be read, so the list below is the last one: ${out.reason ?? 'not known'}`);
    } catch (err) {
      setSyncNote(errorMessage(err));
    } finally {
      setSyncing(false);
    }
  }

  const columns = [
    col.accessor((d) => d.docId, {
      id: 'doc',
      header: 'Document',
      cell: (c) => (
        <Link to={`/office/documents/${c.row.original.id}`} className={`font-mono underline ${officeFocus}`}>
          {c.getValue()}
        </Link>
      ),
      meta: { rowHeader: true, nowrap: true },
    }),
    col.accessor((d) => d.title, { id: 'title', header: 'Title' }),
    col.accessor((d) => d.typeCode, { id: 'type', header: 'Type', meta: { nowrap: true } }),
    col.accessor((d) => d.processCode, { id: 'process', header: 'Process', meta: { nowrap: true } }),
    col.accessor((d) => d.status, {
      id: 'status',
      header: 'Status',
      cell: (c) => {
        const d = c.row.original;
        return (
          <span className={`${chipClass} w-fit ${TONE_CLASS[d.retired ? 'quiet' : STATUS_TONE[d.status]]}`}>
            {d.retired ? 'Retired' : STATUS_LABEL[d.status]}
            {d.versionNo ? ` · v${d.versionNo}` : ''}
          </span>
        );
      },
      meta: { nowrap: true },
    }),
    col.accessor((d) => d.ownerRole, { id: 'owner', header: 'Owner' }),
    col.accessor((d) => d.reviewBy ?? '', { id: 'review', header: 'Review by', cell: (c) => c.getValue() || '—', meta: { nowrap: true } }),
    col.accessor((d) => d.ack?.owed ?? -1, {
      id: 'read',
      header: 'Read',
      cell: (c) => {
        const a = c.row.original.ack;
        return a ? `${a.acknowledged} of ${a.applies}` : '—';
      },
      meta: { align: 'right', nowrap: true },
    }),
    col.accessor((d) => attentionOf(d).join(', '), {
      id: 'attention',
      header: 'Needs',
      cell: (c) => c.getValue() || null,
    }),
  ];

  if (config.error) {
    return (
      <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
        <p className="m-0 font-semibold text-(--warn)">Could not read whether documents are switched on: {config.error}</p>
        <Button variant="outline" type="button" onClick={config.reload}>
          Try again
        </Button>
      </div>
    );
  }
  if (config.data && !config.data.enabled) {
    return (
      <div className="flex flex-col gap-(--s2)">
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Documents</h1>
        <p className={muted}>Document control is not switched on yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-(--s5)">
      <header>
        <h1 className="m-0 font-display text-[34px] font-semibold leading-none tracking-[-0.01em]">Documents</h1>
        <p className={`${muted} mt-(--s1) max-w-[80ch]`}>Every controlled quality document: what is in force, who owns it, when its review falls due and who has read it. The master lives in SharePoint; approving it here makes the record.</p>
        <div className="mt-(--s2) flex flex-wrap items-center gap-(--s2)">
          {mayAuthor && hasTemplate ? (
            <Button type="button" onClick={() => setRegistering({ file: null })} disabled={!config.data?.siteConfigured}>
              New document
            </Button>
          ) : null}
          {(mayAuthor || mayApprove) && config.data?.siteConfigured ? (
            <Button type="button" variant="outline" disabled={syncing} onClick={() => void sync()}>
              {syncing ? 'Reading the library…' : 'Read the library now'}
            </Button>
          ) : null}
          {mayAudit ? (
            <Link to="/office/documents/audit" className={`text-(length:--fs-sm) underline ${officeFocus}`}>
              What was in force on a date
            </Link>
          ) : null}
        </div>
      </header>

      {config.data && !config.data.siteConfigured ? <p className={muted}>The QMS site is not set up in this app yet, so no file can be read, registered or issued.</p> : null}
      {library?.configured && !library.syncedAt && !library.lastFailure ? <p className={muted}>The library has not been read yet, so nothing is known about the files in it.</p> : null}
      {library?.syncedAt ? <p className={muted}>Library last read {stamp(library.syncedAt)}.</p> : null}
      {library?.lastFailure ? (
        <p role="alert" className="m-0 font-semibold text-(--warn)">
          The last read of the library failed ({stamp(library.lastFailure.at)}): {library.lastFailure.reason} What follows is older than it looks.
        </p>
      ) : null}
      {syncNote ? (
        <p role="alert" className="m-0 font-semibold text-(--warn)">
          {syncNote}
        </p>
      ) : null}

      {res.data ? <TaxonomyPanel tax={tax} mayEdit={mayApprove} onDone={res.reload} /> : null}
      {res.data ? (
        <UnregisteredFiles
          files={res.data.unregistered}
          mayWrite={(mayAuthor || mayApprove) && canRegister}
          onRegister={(file) => setRegistering({ file })}
          onDone={res.reload}
        />
      ) : null}

      <div className="flex flex-wrap items-end gap-(--s3)">
        <label className="flex w-fit flex-col gap-(--s1)">
          <span className={labelClass}>Status</span>
          <select className={`${fieldClass} ${officeFocus}`} value={status} onChange={(e) => setStatus(e.target.value as ShownStatus | 'ANY')}>
            <option value="ANY">Any</option>
            {(Object.keys(STATUS_LABEL) as ShownStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex min-h-[44px] items-center gap-(--s2) text-(length:--fs-sm)">
          <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired
        </label>
      </div>

      {res.error ? (
        <div role="alert" className="flex flex-wrap items-center gap-(--s2)">
          <p className="m-0 font-semibold text-(--warn)">Could not read the register: {res.error} This is not the same as "none".</p>
          <Button variant="outline" type="button" onClick={res.reload}>
            Try again
          </Button>
        </div>
      ) : (
        <DataTable
          data={rows}
          columns={columns}
          caption="Controlled documents, by number"
          getRowId={(d) => String(d.id)}
          rowAccent={(d) => (!d.retired && (d.standing.reviewState === 'OVERDUE' || d.standing.masterMissing) ? 'bad' : !d.retired && (d.standing.editedOutsideADraft || d.standing.changedInReview) ? 'warn' : null)}
          loading={res.loading || config.loading}
          stickyFirst
          search={(res.data?.documents.length ?? 0) > 10 ? 'Search number, title or owner' : undefined}
          empty={res.data ? 'No documents are registered yet.' : null}
        />
      )}

      {registering ? <RegisterSheet file={registering.file} tax={tax} config={config.data} onClose={() => setRegistering(null)} onDone={res.reload} /> : null}
    </div>
  );
}

export default Documents;
